import { computed, readonly, ref, toValue, watch, type MaybeRefOrGetter } from 'vue';
import type { SearchFilterPayload } from '@/composables/api/SearchApi';
import { postMatchingScheduleCount } from '@/composables/api/useBulkActionApi';

export interface UseMatchingScheduleCountOptions {
  /** The search page's reset key. A new key means a new search, so the old count is dropped. */
  resetKey: MaybeRefOrGetter<unknown>;
  /** The filter the page searched with, as the search request posts it. Read when the count is asked for. */
  filter: MaybeRefOrGetter<SearchFilterPayload | null>;
}

/**
 * How many schedules pass the custom list search on screen, for the bulk bar's
 * "Select all matching 40 custom lists / 112 schedules".
 *
 * Nothing is fetched until {@link request} is called. The page calls it when the bulk bar's top
 * row first appears, so an ordinary page load never runs the count.
 *
 * Each search is counted once, until {@link invalidate} drops the count.
 *
 * A new reset key clears the count. An answer that arrives after the key changed is thrown away.
 * For example, a user filters on "xmas", ticks a list, and then filters on "easter" before the
 * count comes back. The "xmas" count is never shown for "easter".
 */
export function useMatchingScheduleCount(options: UseMatchingScheduleCountOptions) {
  const count = ref<number | null>(null);
  const isLoading = ref(false);
  const isError = ref(false);
  /** Goes up with each new search. An answer is kept only when it was asked for in the current one. */
  let searchGeneration = 0;
  /** True once this search's count was asked for, so a second request does not ask again. */
  let requestedInThisSearch = false;

  function request() {
    if (requestedInThisSearch) return;
    requestedInThisSearch = true;
    const askedInGeneration = searchGeneration;
    isLoading.value = true;
    isError.value = false;

    postMatchingScheduleCount(toValue(options.filter) ?? null).then(
      (answer) => {
        if (askedInGeneration !== searchGeneration) return;
        count.value = answer;
        isLoading.value = false;
      },
      () => {
        if (askedInGeneration !== searchGeneration) return;
        // The bar says the count failed, so the user is not left with a number that never comes.
        // The next request asks again.
        isLoading.value = false;
        isError.value = true;
        requestedInThisSearch = false;
      },
    );
  }

  /**
   * Drop the count, so the next {@link request} asks again. A bulk run that finished may have
   * deleted or changed schedules, so the same search can now match fewer. Without this, select
   * all would send the old number as `expected_count` and the server would answer 409.
   */
  function invalidate() {
    searchGeneration += 1;
    requestedInThisSearch = false;
    count.value = null;
    isLoading.value = false;
    isError.value = false;
  }

  watch(() => toValue(options.resetKey), invalidate);

  return {
    count: readonly(count),
    isLoading: readonly(isLoading),
    isError: readonly(isError),
    /** True while there is no number to show yet: loading, failed, or never asked for. */
    isUnknown: computed(() => count.value === null),
    request,
    invalidate,
  };
}

export type MatchingScheduleCount = ReturnType<typeof useMatchingScheduleCount>;
